import React from "react";
import { motion } from "framer-motion";
import { Check, Clock } from "lucide-react";

const tiers = [
  {
    name: "Essential Protection",
    price: "$4,500 – $6,500",
    size: "Approx. 1,000–2,000 sq. ft.",
    blurb: "For homeowners primarily concerned with catastrophic water-damage protection.",
    features: [
      "Main water-system monitoring",
      "Burst-pipe detection",
      "Automatic electronic water shutoff",
      "Redundant shutoff architecture",
      "Dual independent actuator controls",
      "Battery backup",
      "Local system controller",
      "Mobile or local alert capability",
      "Basic system-health monitoring",
    ],
    example: "A 1,700 sq. ft. home with one primary water service and a straightforward plumbing layout may fall within this range.",
  },
  {
    name: "Advanced Home Protection",
    price: "$8,500 – $14,000",
    size: "Approx. 2,000–4,000 sq. ft.",
    featured: true,
    blurb: "For larger homes requiring broader mechanical-system monitoring and more advanced automation.",
    features: [
      "Everything in Essential Protection",
      "Multiple water-monitoring points",
      "Additional leak or pressure sensors",
      "HVAC & equipment-health monitoring",
      "Condensate / abnormal-operation detection",
      "Additional environmental sensors",
      "Advanced alert programming",
      "Expanded battery backup",
      "Smart-home & Alexa integration",
      "Local network dashboard",
      "Custom automatic-response programming",
    ],
    example: "A 3,200 sq. ft. home with two HVAC systems, multiple plumbing zones, and whole-home monitoring may fall within this range.",
  },
  {
    name: "Total Home Intelligence",
    price: "$18,000 – $30,000+",
    size: "Approx. 4,000–8,000+ sq. ft.",
    blurb: "For luxury homes and families wanting comprehensive protection and our complete Home Health AI platform.",
    features: [
      "Full-property water monitoring",
      "Multiple independent shutoff locations",
      "Redundant actuator & control architecture",
      "Multiple HVAC monitoring stations",
      "Environmental & equipment-room monitoring",
      "Local AI processing",
      "Custom automation sequences",
      "Property-wide health monitoring",
      "Occupant monitoring & fall detection",
      "Heart-rate, breathing & temperature monitoring",
      "Automated emergency-contact sequences",
    ],
    example: "A 5,500 sq. ft. home with three HVAC systems, multiple plumbing zones, and Home Health AI integration may fall within this range.",
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="relative py-24 lg:py-36 border-t border-white/5" data-testid="pricing-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="overline mb-4">System Packages &amp; Example Pricing</div>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mb-5">
          Serious Protection. <span className="gold-fill">Engineered to the Property.</span>
        </h2>
        <p className="text-white/60 text-lg leading-relaxed max-w-3xl mb-14">
          Every home is different — plumbing layout, square footage, HVAC systems, sensor requirements and desired
          automation all affect final design and cost. These examples help you understand the general investment level
          before requesting a consultation. Final pricing is determined after a property review and system design.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`pricing-tier-${i}`}
              className={`rounded-2xl p-8 flex flex-col ${t.featured ? "glass border border-[var(--gold)]/50 shadow-[0_0_60px_rgba(214,164,65,0.12)]" : "glass border border-white/10"}`}
            >
              {t.featured && <div className="overline text-[var(--gold)] mb-3">Most Popular</div>}
              <h3 className="font-display text-2xl text-white mb-1">{t.name}</h3>
              <div className="text-sm text-white/45 mb-5">{t.size}</div>
              <div className="font-display text-3xl gold-fill mb-1">{t.price}</div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-white/40 mb-6">Starting Around</div>
              <p className="text-white/60 text-sm leading-relaxed mb-6">{t.blurb}</p>
              <ul className="space-y-2.5 mb-7 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/75">
                    <Check size={15} className="text-[var(--gold)] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/40 leading-relaxed border-t border-white/10 pt-4 mb-6">{t.example}</p>
              <button
                onClick={() => { const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
                data-testid={`pricing-cta-${i}`}
                className={`${t.featured ? "btn-gold" : "btn-ghost"} rounded-full px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-medium`}
              >
                Request a Consultation
              </button>
            </motion.div>
          ))}
        </div>

        {/* Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
          <div>
            <h4 className="font-display text-xl text-white mb-3">Why We Show Pricing</h4>
            <p className="text-white/60 text-sm leading-relaxed">
              We believe homeowners should know the approximate investment before scheduling a consultation. These are not
              $199 smart-home gadgets. Our systems combine professional-grade sensors, redundant electronic shutoff hardware,
              local processing, battery backup, mechanical-system monitoring, automation, and professional installation —
              protecting against losses that can easily reach tens of thousands of dollars when failures go undetected.
            </p>
          </div>
          <div>
            <h4 className="font-display text-xl text-white mb-3">Your Property Determines the Final Design</h4>
            <p className="text-white/60 text-sm leading-relaxed">
              Square footage is a starting point, not the price. Final pricing depends on plumbing zones, number of HVAC
              systems, shutoff valve count and locations, sensor quantity, mechanical-room configuration, accessibility,
              battery-backup requirements, smart-home integration, Home Health AI options, and custom automation.
            </p>
          </div>
        </div>

        <div className="glass rounded-2xl p-8 sm:p-10 mt-10 flex items-start gap-4 border border-[var(--gold)]/20" data-testid="emergency-guarantee">
          <Clock size={26} className="text-[var(--gold)] shrink-0 mt-1" />
          <div>
            <h4 className="font-display text-2xl text-white mb-2">365 Days a Year. 24 Hours a Day.</h4>
            <p className="text-white/70 leading-relaxed max-w-3xl">
              Our systems come with a dedicated emergency service team, available around the clock. If for any reason you
              need us to come out, we will — any time of the day or night, guaranteed. Response time is within minutes.
              <span className="block mt-3 text-[var(--gold)] font-display text-lg">Protect the property. Protect the equipment. Protect the people inside it.</span>
            </p>
          </div>
        </div>

        <p className="text-white/45 text-sm leading-relaxed max-w-3xl mt-10">
          Most professionally installed systems begin at approximately $4,500. More comprehensive whole-home installations
          typically range from approximately $8,500 to $30,000+, depending on the property and desired level of protection.
          Large estates, guest houses, detached structures and highly customized installations are quoted individually.
        </p>
      </div>
    </section>
  );
}
